import { coordinatesToCoords, getFullAddressLabel } from '../../../common/utils';
import { getIntl } from '../../../configuration/setup/intl';
import { store } from '../../../configuration/setup/store';
import { trace } from '../../../configuration/setup/trace';
import { getConnectorCurrentAndVoltageByPower } from '../../../features/search/ExtendedOptions';
import type { ChargePoint, ChargingStation, Connector } from '../../facade/facadeApiV3';
import { facadeApiV3 } from '../../facade/facadeApiV3';
import { selectIsAutomatedPlanningMode, selectOrderedWaypoints, selectWaypoints } from '../../search/searchSelectors';
import { type Waypoint, WaypointType, waypointAdded, waypointChanged } from '../../search/searchSlice';
import { NotificationType, showMapNotification } from '../actions/showMapNotification';

export const ADD_ROUTE_STATION_EVENT = 'EVENT_ADD_ROUTE_STATION';

const fetchStationData = async (stationId: string): Promise<ChargingStation | undefined> => {
    const result = store.dispatch(facadeApiV3.endpoints.getChargingStation.initiate({ id: stationId }));
    try {
        return await result.unwrap();
    } catch (error) {
        console.error('Failed to fetch charging station data:', error);
        return undefined;
    } finally {
        result.unsubscribe();
    }
};

export const addStationWaypointHandler = async (stationData: ChargingStation) => {
    const intl = getIntl();
    const isAutomatedPlanningMode = selectIsAutomatedPlanningMode(store.getState());

    if (isAutomatedPlanningMode) {
        showMapNotification(
            NotificationType.WARNING,
            intl.formatMessage({ id: 'intl-msg:smartRoute.chargingStation.NotAddedStationToRoute' }),
            intl.formatMessage({ id: 'intl-msg:smartRoute.tour.turnOffAutoMode' })
        );
        return;
    }

    let resolvedStationData = stationData;

    if (!stationData?.chargePoints) {
        const stationId = stationData.markerData.station_id;

        if (!stationId) {
            trace.error(`No station data for stationID: ${stationId}`);
            return;
        }
        const fetchedData = await fetchStationData(stationId);
        if (!fetchedData) {
            return;
        }
        resolvedStationData = fetchedData;
    }

    const waypoints = selectWaypoints(store.getState());
    const isWaypointDuplicated = waypoints.some(
        wp => wp.addressLabel === getFullAddressLabel(resolvedStationData.address)
    );

    if (isWaypointDuplicated) {
        showMapNotification(
            NotificationType.WARNING,
            intl.formatMessage({ id: 'intl-msg:smartRoute.chargingStation.stationAlreadyOnRoute' }),
            intl.formatMessage({ id: 'intl-msg:smartRoute.tour.selectAnotherStation' })
        );
        return;
    }

    const orderedWaypoints = selectOrderedWaypoints(store.getState());
    const lastWaypoint = orderedWaypoints.at(-1);

    const allConnectors: Connector[] = resolvedStationData?.chargePoints?.flatMap(
        (chargePoint: ChargePoint) => chargePoint?.connectors ?? []
    );

    const result = getConnectorCurrentAndVoltageByPower(allConnectors, resolvedStationData.maxPower ?? 0);
    const current = result?.current;
    const voltage = result?.voltage;

    store.dispatch(waypointAdded(-1));
    store.dispatch(
        waypointChanged({
            id: store.getState().search.waypointsOrder.at(-2),
            addressLabel: getFullAddressLabel(resolvedStationData.address),
            coordinates: coordinatesToCoords(resolvedStationData.location),
            type: WaypointType.CHARGING_STATION,
            connectors: allConnectors,
            maxPower: resolvedStationData.maxPower,
            current,
            voltage,
            station_id: resolvedStationData.id,
        } as Waypoint)
    );

    if (lastWaypoint) {
        store.dispatch(
            waypointChanged({
                id: store.getState().search.waypointsOrder.at(-1),
                addressLabel: lastWaypoint.addressLabel,
                coordinates: lastWaypoint.coordinates,
                type: lastWaypoint.type,
            } as Waypoint)
        );
    }

    showMapNotification(
        NotificationType.INFO,
        intl.formatMessage({ id: 'intl-msg:smartRoute.chargingStation.addedStationToRoute' })
    );
};
